import { useState } from "react";
import { Container, Nav, Navbar as NavbarComp, Offcanvas } from "react-bootstrap";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../hooks/useUser";

const HIDE_BACK_ON = new Set(["/", "/home", "/login", "/register"]);

function getSections({ user, isAdminAuthenticated, isModeratorAuthenticated, canViewBots }) {
  if (!user) return [];

  const sections = [
    {
      title: "Main",
      items: [
        { to: "/home", icon: "🏠", label: "Home" },
        { to: "/profile", icon: "👤", label: "Profile" },
      ],
    },
    {
      title: "Alerts",
      items: [
        { to: "/alertspage", icon: "🔔", label: "Alerts" },
        { to: "/shortList", icon: "⭐", label: "Short List" },
        { to: "/charts", icon: "📈", label: "Charts" },
        { to: "/configAlerts", icon: "⚙️", label: "Alert Config" },
      ],
    },
    {
      title: "Trade Journal",
      items: [
        { to: "/trade-journal/all-trades", icon: "📓", label: "All Trades" },
        { to: "/trade-journal/add-trade", icon: "➕", label: "Add Trade" },
      ],
    },
  ];

  if (isModeratorAuthenticated) {
    sections.push({
      title: "Moderator",
      items: [{ to: "/mod", icon: "🛡️", label: "Moderator Board" }],
    });
  }

  // Bot-operations pages are read-only, so viewers get them too. Kept as
  // its own section so the Admin block below stays strictly privileged.
  if (canViewBots) {
    sections.push({
      title: "Trading Bots",
      items: [
        { to: "/agent", icon: "🤖", label: "Trading Agent" },
        { to: "/observability", icon: "📊", label: "Observability" },
        { to: "/bot-trades", icon: "💹", label: "Bot Trades" },
        { to: "/agents", icon: "📖", label: "Agents Reference" },
      ],
    });
  }

  if (isAdminAuthenticated) {
    sections.push({
      title: "Admin",
      items: [
        { to: "/admin/users", icon: "👥", label: "All Users" },
        { to: "/admin/registrationRequests", icon: "✉️", label: "Registration Requests" },
        { to: "/admin/invitation", icon: "📨", label: "Invitations" },
      ],
    });
  }

  return sections;
}

function roleLabel({ isAdminAuthenticated, isModeratorAuthenticated, isViewerAuthenticated }) {
  if (isAdminAuthenticated) return "Admin";
  if (isModeratorAuthenticated) return "Moderator";
  if (isViewerAuthenticated) return "Viewer (read-only)";
  return "Member";
}

function Navbar({ logOut }) {
  const userCtx = useUser();
  const { user } = userCtx;
  const [show, setShow] = useState(false);
  const close = () => setShow(false);
  const location = useLocation();
  const navigate = useNavigate();

  const sections = getSections(userCtx);
  const showBack = user && !HIDE_BACK_ON.has(location.pathname);

  return (
    <NavbarComp expand={false} className="navbar-dark bg-dark fixed-top app-navbar">
      <Container fluid>
        {user && (
          <NavbarComp.Toggle
            aria-controls="app-drawer"
            onClick={() => setShow(true)}
            className="border-0 me-2"
          />
        )}
        {showBack && (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="nav-back-btn"
            aria-label="Go back"
            title="Back"
          >
            <span aria-hidden="true">‹</span>
          </button>
        )}
        <NavbarComp.Brand as={NavLink} to={user ? "/home" : "/"} className="m-0 fw-bold">
          Shoolin
        </NavbarComp.Brand>

        <Nav className="ms-auto flex-row align-items-center gap-2">
          {!user && (
            <>
              <NavLink to="/login" className="btn btn-outline-light btn-sm">
                Login
              </NavLink>
              <NavLink to="/register" className="btn btn-success btn-sm">
                Sign Up
              </NavLink>
            </>
          )}
        </Nav>

        {user && (
          <NavbarComp.Offcanvas
            id="app-drawer"
            show={show}
            onHide={close}
            placement="start"
            className="bg-dark text-light app-drawer"
          >
            <Offcanvas.Header closeButton closeVariant="white">
              <Offcanvas.Title className="fw-bold">Shoolin</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
              <div className="drawer-user">
                <div className="drawer-user-name">{user.username}</div>
                <div className="drawer-user-role">{roleLabel(userCtx)}</div>
              </div>

              {sections.map((section) => (
                <div key={section.title} className="drawer-section">
                  <div className="drawer-section-title">{section.title}</div>
                  <ul className="drawer-list">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <NavLink to={item.to} className="drawer-link" onClick={close}>
                          <span className="drawer-icon">{item.icon}</span>
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="drawer-footer">
                <button
                  type="button"
                  className="btn btn-outline-light w-100"
                  onClick={() => {
                    close();
                    logOut();
                  }}
                >
                  Log out
                </button>
              </div>
            </Offcanvas.Body>
          </NavbarComp.Offcanvas>
        )}
      </Container>
    </NavbarComp>
  );
}

export default Navbar;
